// ═══════════════════════════════════════════════════════════════════════════
// PasswordResetRequestPage — Phase 2.2 step 1.
//
// Asks the user for their email and triggers Supabase to send the recovery
// link. Always shows the success message regardless of whether the email
// exists in the database — surfacing "no account with this email" here
// would be a user-enumeration leak.
//
// The recovery link in the email lands on `/auth/reset-password` (handled
// by PasswordResetConfirmPage) with a temporary recovery session.
// ═══════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { requestPasswordReset } from '../../services/authApi';
import { mapAuthError } from '../../services/errorMap';
import { analytics } from '../../services/analytics';
import { AuthLayout } from '../../components/auth/AuthLayout';
import styles from './auth-page.module.css';

type Status = 'idle' | 'sending' | 'sent' | 'error';

export function PasswordResetRequestPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || status === 'sending') return;
    setStatus('sending');
    setError(null);
    analytics.event('auth_password_reset_request');
    try {
      await requestPasswordReset(email.trim());
      // Success path: same UI whether the email exists or not (anti-
      // enumeration). Supabase returns 200 in both cases by design.
      setStatus('sent');
      analytics.event('auth_password_reset_sent');
    } catch (err) {
      const mapped = mapAuthError(err);
      // Real failure (network, etc.) — user can retry.
      setError(t(mapped.messageKey, mapped.messageDefault));
      setStatus('error');
      analytics.event('auth_password_reset_failed', { code: mapped.code });
    }
  };

  return (
    <AuthLayout cardMaxWidth={420} cardPadding="32px" background="var(--bg)">
      <h1 className={styles.title}>
        {t('auth.actions.reset', 'Reset password')}
      </h1>
      <p className={styles.subtitle}>
        {t('auth.passwordResetRequestSubtitle', {
          defaultValue:
            "Entre ton email — on t'envoie un lien pour créer un nouveau mot de passe.",
        })}
      </p>

      {status === 'sent' ? (
        <div role="status" aria-live="polite">
          <p className={styles.successText}>
            ✓ {t('auth.hints.checkInbox', 'Un lien de vérification a été envoyé à ta boîte de réception.')}
          </p>
          <p className={styles.helpText}>
            {t('auth.passwordResetSentExplain', {
              defaultValue:
                "Si un compte existe avec cet email, tu vas recevoir un lien dans quelques minutes. Pense à vérifier tes spams.",
            })}
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <label htmlFor="reset-email" className={styles.fieldLabel}>
            {t('auth.email', 'Email')}
          </label>
          <input
            id="reset-email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.emailPlaceholder', 'votre@email.com')}
            disabled={status === 'sending'}
            className={styles.input}
          />
          <button
            type="submit"
            disabled={!email.trim() || status === 'sending'}
            aria-busy={status === 'sending'}
            className={styles.primaryButton}
          >
            {status === 'sending' ? t('common.loading', 'Loading…') : t('auth.actions.reset', 'Reset password')}
          </button>
          {error && (
            <p role="alert" className={styles.errorText}>
              {error}
            </p>
          )}
        </form>
      )}

      <div className={styles.footer}>
        <Link to="/auth/login" className={styles.linkAccent}>
          ← {t('auth.actions.login', 'Sign in')}
        </Link>
      </div>
    </AuthLayout>
  );
}

export default PasswordResetRequestPage;
